import CalendarIcon1 from '@forwardflow/assets/svg/CalenderIcon1'
import { useGetDashboardChartsOverview } from '@forwardflow/hooks/customHooks'
import { SourceInfo, TimePeriod } from '@forwardflow/types'
import { Skeleton } from 'antd'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const chartColors = ['#08BDAA', '#FACF71', '#F98D56', '#F86A47', '#e93407']

interface TooltipData {
   x: number
   y: number
   data: SourceInfo
}

const CustomPieChart = ({
   data,
   hoveredIndex,
}: {
   data: SourceInfo[]
   hoveredIndex: number | null
}) => {
   const [hoveredSegment, setHoveredSegment] = useState<number | null>(null)
   const [tooltipData, setTooltipData] = useState<TooltipData | null>(null)

   const centerX = 100
   const centerY = 100
   const outerRadius = 75
   const innerRadius = 62

   // Calculate angles for each segment
   const total = data.reduce((sum, item) => sum + item.count, 0)
   let currentAngle = -90 // Start from top

   const segments = data.map((item, index) => {
      const percentage = item.count / total
      const angle = percentage * 360
      const startAngle = currentAngle
      const endAngle = currentAngle + angle
      currentAngle = endAngle

      // Convert angles to radians
      const startRad = (startAngle * Math.PI) / 180
      const endRad = (endAngle * Math.PI) / 180

      // Calculate path coordinates
      const x1 = centerX + outerRadius * Math.cos(startRad)
      const y1 = centerY + outerRadius * Math.sin(startRad)
      const x2 = centerX + outerRadius * Math.cos(endRad)
      const y2 = centerY + outerRadius * Math.sin(endRad)
      const x3 = centerX + innerRadius * Math.cos(endRad)
      const y3 = centerY + innerRadius * Math.sin(endRad)
      const x4 = centerX + innerRadius * Math.cos(startRad)
      const y4 = centerY + innerRadius * Math.sin(startRad)

      const largeArcFlag = angle > 180 ? 1 : 0

      const path = `
         M ${x1} ${y1}
         A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 1 ${x2} ${y2}
         L ${x3} ${y3}
         A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}
         Z
      `

      return {
         path,
         item,
         index,
         percentage: Math.round(percentage * 100),
      }
   })

   const handleMouseMove = (e: React.MouseEvent, segment: any) => {
      const rect = e.currentTarget.getBoundingClientRect()
      setTooltipData({
         x: e.clientX - rect.left,
         y: e.clientY - rect.top,
         data: segment.item,
      })
   }

   return (
      <div className="relative w-full max-w-[300px] aspect-square mx-auto">
         <svg
            viewBox="0 0 200 200"
            className="w-full h-full"
            onMouseLeave={() => {
               setHoveredSegment(null)
               setTooltipData(null)
            }}
         >
            {segments.map((segment, index) => {
               const isHighlighted =
                  hoveredSegment === segment.index ||
                  hoveredIndex === segment.index

               return (
                  <path
                     key={segment.index}
                     d={segment.path}
                     fill={chartColors[index]}
                     stroke="none"
                     opacity={
                        isHighlighted
                           ? 1
                           : hoveredSegment !== null || hoveredIndex !== null
                           ? 0.4
                           : 1
                     }
                     style={{
                        transition: 'opacity 0.2s ease-in-out',
                        cursor: 'pointer',
                     }}
                     onMouseEnter={() => setHoveredSegment(segment.index)}
                     onMouseMove={(e) => handleMouseMove(e, segment)}
                  />
               )
            })}
         </svg>

         {/* Tooltip */}
         {tooltipData && (
            <div
               className="absolute z-10 px-3 py-2 bg-gray-800 rounded shadow-lg pointer-events-none"
               style={{
                  left: `${tooltipData.x}px`,
                  top: `${tooltipData.y - 40}px`,
               }}
            >
               <p className="text-sm text-white whitespace-nowrap">
                  {tooltipData.data.name}: {tooltipData.data.count}
               </p>
            </div>
         )}
      </div>
   )
}

const MainCard = () => {
   const [searchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'
   const {
      data: DashboardChartsOverview,
      isLoading: DashboardChartsOverviewLoading,
   } = useGetDashboardChartsOverview({
      timePeriod: timePeriod as TimePeriod,
   })
   const [hoveredLegendIndex, setHoveredLegendIndex] = useState<number | null>(
      null
   )

   if (DashboardChartsOverviewLoading) {
      return (
         <div className="space-y-4">
            <Skeleton paragraph />
            <Skeleton paragraph />
         </div>
      )
   }

   // Destructure mainCard data to avoid drilling
   const {
      total: apiTotal,
      sources,
      change,
      description,
      displayTitle,
   } = DashboardChartsOverview?.mainCard || {}

   // Destructure nested objects
   const { percent: growthPercentage = 0, isIncrement } = change || {}
   const { highlight, text: descriptionText } = description || {}

   return (
      <div className="flex flex-col h-auto p-3 rounded-lg lg:flex-row bg-white/10">
         {/* Left Section - Appointments */}
         <div className="flex flex-col md:items-center md:justify-center p-2 sm:py-5 sm:px-[5%] lg:py-0">
            <div className="!w-[60px] p-2 mb-4 bg-white/10 rounded-xl">
               <CalendarIcon1 />
            </div>
            <div className="mb-2">
               <h2 className="flex items-baseline gap-2 text-3xl font-medium text-white sm:text-4xl lg:text-5xl">
                  <span>{apiTotal}</span>
                  <span className="sm:hidden">
                     {change && (
                        <div
                           className={`flex md:items-center ${
                              isIncrement ? 'text-[#00C2A8]' : 'text-red-500'
                           }  w-full md:justify-center pb-2`}
                        >
                           <span className="text-sm font-medium sm:text-base">
                              {isIncrement ? '+' : '-'}
                              {growthPercentage}%
                           </span>
                        </div>
                     )}
                  </span>
               </h2>
            </div>
            <div className="mb-2">
               <p className="text-base sm:text-lg">{displayTitle}</p>
            </div>
            {change && (
               <div
                  className={`md:items-center hidden sm:flex ${
                     isIncrement ? 'text-[#00C2A8]' : 'text-red-500'
                  } border-b border-white/10 w-full md:justify-center pb-2`}
               >
                  <span className="text-sm font-medium sm:text-base">
                     {isIncrement ? '+' : '-'}
                     {growthPercentage}%
                  </span>
               </div>
            )}
            <div className="mt-2 md:text-center">
               <p className="text-xs sm:text-sm text-wrap max-w-48">
                  <span
                     className={`${
                        isIncrement ? 'text-[#00C2A8]' : 'text-red-500'
                     } font-medium `}
                  >
                     {highlight}
                  </span>{' '}
                  {descriptionText}
               </p>
            </div>
         </div>

         {/* Right Section - Donut Chart and Legend */}
         <div className="flex items-center flex-1 p-2 mt-2 rounded-lg sm:mt-5 sm:flex-row sm:px-4 sm:pl-0 bg-black/10 lg:mt-0 max-h-[280px]">
            <div className="relative w-full sm:w-1/2 lg:flex-1 sm:mb-0 ">
               <CustomPieChart
                  data={sources || []}
                  hoveredIndex={hoveredLegendIndex}
               />
            </div>

            {/* Legend */}
            <div className="w-full space-y-1 sm:w-1/2 lg:flex-1 sm:space-y-2">
               {sources?.map((item, index) => {
                  const isHovered = hoveredLegendIndex === index
                  const faded = hoveredLegendIndex !== null && !isHovered
                  return (
                     <div
                        key={index}
                        className="flex items-center justify-between px-2 min-w-[180px] sm:px-3 py-1.5 transition-all rounded cursor-pointer"
                        style={{
                           backgroundColor: `${chartColors[index]}${Math.round(
                              (0.3 - index * 0.05) * 255
                           )
                              .toString(16)
                              .padStart(2, '0')}`,
                           opacity: isHovered ? 1 : faded ? 0.4 : 1,
                        }}
                        onMouseEnter={() => setHoveredLegendIndex(index)}
                        onMouseLeave={() => setHoveredLegendIndex(null)}
                     >
                        <div className="flex items-center gap-2 sm:gap-3">
                           <div
                              className="w-2.5 sm:w-3 h-2.5 sm:h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: chartColors[index] }}
                           />
                           <span className="text-xs sm:text-sm">
                              {item.name}
                           </span>
                        </div>
                        <div className="flex flex-col items-center">
                           <span className="text-xs font-semibold text-white sm:text-sm">
                              {item.count}
                           </span>
                           <span className="text-[10px] sm:text-xs text-white/70 hidden sm:block">
                              {item.percent}%
                           </span>
                        </div>
                     </div>
                  )
               })}
            </div>
         </div>
      </div>
   )
}

export default MainCard
